import { useId } from 'react';

/**
 * The two terminal icons of the app, in colour, so that they sit with the
 * official GitLab, ArgoCD and Grafana logos (brandIcons.tsx) without looking
 * like the odd one out:
 * - `PodTerminalIcon`, a pod's terminal wherever it is offered (the icons
 *   registry's `terminal` key): a badge in the Waadoo orange, a white prompt;
 * - `OdooShellIcon`, the « Ouvrir un Odoo shell » button only (the
 *   `odoo-shell` key): a slate window, a cyan prompt — another tool than a
 *   pod's shell, so another look; in `components/odooIcons/terminalIcons.tsx`
 *   since step 9c.
 *
 * Drawn inline rather than as files through an <image> like the official
 * logos: the dark-mode outline of the Odoo shell window comes from the page's
 * CSS (styles.css, `.waadoo-odoo-shell-icon__window`), which cannot reach
 * into an image. Their colours are fixed, never `currentColor`, so no colour
 * set around them changes them. Same element contract as the logos: an
 * <svg> of 1em on a 24x24 viewBox, decorative.
 */

/**
 * A gradient id unique to one rendered icon. Many terminal icons share a page
 * (one per pod row), and a duplicate id would make every `url(#…)` point at
 * the first gradient of that id in the document. React's useId gives one id
 * per instance; its colons (`:r1:`) are dropped because they are not valid in
 * the fragment of a `url(#…)` reference without escaping. Shared with the
 * other inline colour icons (components/odooIcons/webIcons.tsx, components/odooIcons/terminalIcons.tsx).
 */
export function useGradientId(prefix: string): string {
  return `${prefix}-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
}

/** The pod terminal: an orange badge, a white `>_` prompt. */
export function PodTerminalIcon() {
  const gradientId = useGradientId('waadoo-terminal-gradient');
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff8a4c" />
          <stop offset="1" stopColor="#e2461c" />
        </linearGradient>
      </defs>
      <rect
        x="1.5"
        y="3"
        width="21"
        height="18"
        rx="5"
        fill={`url(#${gradientId})`}
      />
      <path
        d="M6.2 9l3.4 3-3.4 3"
        fill="none"
        stroke="#fff"
        strokeWidth="2.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="11" y="14" width="7" height="2.3" rx="1.15" fill="#fff" />
    </svg>
  );
}
